import React from "react";
import classNames from "classnames";
// types
import { BaseControlProps } from "../../../types/components/dynamicForms/dynamicField";

type NumberInputProps = BaseControlProps<string> & {
  /** "integer" allows digits only; "decimal" allows a single dot */
  mode: "integer" | "decimal";
  maxLength?: number;
  /** render as password (isSensitive) */
  sensitive?: boolean;
  /** static adornment shown at the end of the field, e.g. a currency ISO */
  suffix?: React.ReactNode;
  className?: string;
};

const DIGITS = /[0-9]/;

/**
 * Presentational numeric input storing a STRING (matches legacy IntegerInput /
 * DecimalField). Controlled, no react-hook-form awareness. Filtering happens on
 * keypress so invalid characters never enter the value; the stored value is a
 * clean numeric string that buildZodSchema validates.
 */
const NumberInput = ({
  id,
  value,
  onChange,
  onBlur,
  disabled,
  describedById,
  invalid,
  placeholder,
  mode,
  maxLength,
  sensitive,
  suffix,
  className,
}: NumberInputProps) => {
  const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const char = e.key;
    if (DIGITS.test(char)) return;
    if (mode === "decimal" && char === "." && !value.includes(".")) return;
    e.preventDefault();
  };

  const input = (
    <input
      id={id}
      type={sensitive ? "password" : "text"}
      inputMode={mode === "decimal" ? "decimal" : "numeric"}
      className={classNames("form-control", "bwp-field__control", className, {
        "bwp-field__control--invalid": invalid,
        "bwp-field__control--has-suffix": Boolean(suffix),
      })}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      onKeyPress={onKeyPress}
      onBlur={onBlur}
      disabled={disabled}
      maxLength={maxLength}
      placeholder={placeholder}
      aria-invalid={invalid || undefined}
      aria-describedby={describedById}
    />
  );

  if (!suffix) return input;

  return (
    <div className="bwp-field__input-group">
      {input}
      <span className="bwp-field__suffix" aria-hidden="true">
        {suffix}
      </span>
    </div>
  );
};

export default NumberInput;